import React from "react";
import { Card, Modal, Button, message, Divider, Flex, Tag, Space, InputNumber } from "antd";
import { 
  PhoneOutlined, 
  ShoppingOutlined,
  NotificationOutlined,
  StopOutlined,
  CarOutlined,
  ArrowRightOutlined,
  BarcodeOutlined,
  HighlightOutlined,
  PayCircleOutlined,
  DoubleRightOutlined,
  EditOutlined
} from "@ant-design/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import dayjs from "dayjs";
import { detectSys } from "../../utils/device";
import { safeCall } from "../../utils/jsbridge";
import { PayFlow, TwoStepFlow, DeliveryFlow } from "./flow";
import Remarks from "./remarks";
import LogisticsDialog from "../../components/logistics-dialog";

const OrderCard = (props) => {
  const { onFlowChange = () => {}, content: order, showActions = true, detail = false, ...others } = props;
  const [logisticsDialogOpen, setLogisticsDialogOpen] = React.useState(false);
  
  if (!order) return null;

  let {
    orderNo,
    thirdOrderNo,
    orderStatus,
    updateTime,
    fromShop,
    quantity,
    createTime,
    payStatus,
    goodsType,
    refundStatus,
    amount,
    shouldAmount,
    realAmount,
    city,
    county,
    sellerRemark,
    goodsId,
    appointment = {},
    shop,
    shopId,
    orderId,
    parentOrderId,
    customer,
    from,
    groupId,
    unit,
    meta: goods,
    shareCustomer,
    pays = [{ amount: 0 }],
    shareShop,
    subOrders = [],
  } = order;

  const mutilOrder = subOrders.length > 1;

  const getSkuInfo = (itemGoods) => {
    const metaInfo = itemGoods?.meta || itemGoods;
    const skuList = metaInfo?.skuList;
    const skuId = metaInfo?.skuId;
    if (skuId && Array.isArray(skuList) && skuList.length > 0) {
      const sku = skuList.find((i) => i?.skuId === skuId);
      if (sku?.title) return sku.title;
      if (sku?.name) return sku.name;
    }
    if (metaInfo?.goodsInfo?.sku) return metaInfo.goodsInfo.sku;
    if (metaInfo?.skuTitle) return metaInfo.skuTitle;
    return null;
  };

  // 根据pays计算真实支付
  const realPayAmount = pays.reduce((total, pay) => {
    total += pay.amount;
    return total;
  }, 0);

  const delivery = async () => { 
    setLogisticsDialogOpen(true);
  };

  const editPrice = () => {
    let inputRef = React.createRef();
    
    const m = Modal.info({
      icon: null,
      closable: true,
      title: "修改价格",
      width: "90%",
      style: { maxWidth: "400px" },
      className: "mobile-price-edit",
      content: (
        <div className="space-y-4 py-2">
          {/* 原价 */}
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">原价</span>
            <span className="text-base font-medium text-gray-700 line-through">¥{(amount/100).toFixed(2)}</span>
          </div>

          {/* 应收 - 可编辑 */}
          <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-700 font-medium">应收金额</span>
              <span className="text-xs text-blue-600">可修改</span>
            </div>
            <InputNumber
              ref={inputRef}
              defaultValue={realAmount/100}
              min={0}
              step={0.01}
              precision={2}
              addonAfter="元"
              className="w-full text-lg font-bold text-red-600"
              size="large"
              controls={false}
            />
          </div>

          {/* 实收 */}
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">已收</span>
            <span className="text-base font-bold text-red-600">¥{(realPayAmount/100).toFixed(2)}</span>
          </div>

          {/* 提示 */}
          <div className="text-xs text-gray-500 text-center pt-2">
            修改后将同步更新应收和实收金额
          </div>
        </div>
      ),
      footer: (
        <div className="flex gap-2">
          <Button 
            block 
            size="large"
            onClick={() => m.destroy()}
            className="flex-1"
          >
            取消
          </Button>
          <Button 
            block 
            size="large"
            type="primary"
            onClick={async () => {
              const inputElement = document.querySelector('.mobile-price-edit .ant-input-number-input');
              const newValue = inputElement?.value;
              console.log(newValue);
              const dRealAmount = parseFloat(newValue) * 100;
              
              if(isNaN(dRealAmount) || dRealAmount < 0) {
                message.error('请输入有效的金额');
                return;
              }
              
              if(dRealAmount === realAmount) {
                m.destroy();
                return;
              }

              try {
                const response = await fetch(`/api/order/update?orderId=${orderId}&shopId=${shopId}`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    realAmount: dRealAmount,
                    shouldAmount: dRealAmount,
                  }),
                });

                const result = await response.json();
                if(result?.success){
                  message.success('价格修改成功！');
                  m.destroy();
                  onFlowChange();
                }else{
                  message.error(result.data || '修改失败');
                }
              } catch (error) {
                message.error('网络错误，请重试');
              }
            }}
            className="flex-1"
          >
            确定
          </Button>
        </div>
      ),
    });
  }

  const fullAddrass = `${(appointment || goods?.appointmentInfo)?.province || ""}${(appointment || goods?.appointmentInfo)?.city || ""}${(appointment || goods?.appointmentInfo)?.county || ""}${(appointment || goods?.appointmentInfo)?.community || ""}${(appointment || goods?.appointmentInfo)?.address || ""}`;

  const actions = showActions ? [
    <a href={`/order/detail?shopId=${shopId}&parentOrderId=${parentOrderId}`} className="text-xs">详情<ArrowRightOutlined key="详情" /></a>
  ] : [];
  
  // 特殊订单打标
  const tags = [];
  // 手工订单
  from = from ? from : "线下";
  tags.push(<Tag key={from} color="blue" className="rounded-full text-xs">{from}</Tag>);

  if(groupId) tags.push(<Tag key='groupbuy' color="cyan" className="rounded-full text-xs">团购</Tag>);

  if (payStatus === 0 && orderStatus === 0 && window.__baseInfo.isSuper) {
    actions.unshift(<span onClick={() => onPayCancel(orderId, shopId)} className="text-xs"><StopOutlined /> 取消</span>);
  }

  if (orderStatus < 12 && !["TIMES_CARD", "GOODS"].includes(goodsType)) {
    actions.unshift(<span style={{color: 'green'}} className="text-xs"><BarcodeOutlined /> <PayFlow order={order} /></span>);
  }
  
  if(goodsType === 'GOODS') {
    if(orderStatus < 8 ){
      actions.unshift(<span style={{color: 'green'}} onClick={delivery} className="text-xs"><CarOutlined /> 发货</span>);
    }else if(orderStatus == 8) {
      actions.unshift(<span style={{color: 'green'}} onClick={delivery} className="text-xs"><CarOutlined /> 发货修改</span>);
    }
  }

  // 订单取消
  if (orderStatus === 14) {
    tags.push(<Tag key={orderStatus} color="orange" className="rounded-full text-xs">已取消</Tag>);
  } else if (payStatus === 0) {
    tags.push(<Tag color="orange" key={payStatus} className="rounded-full text-xs">待支付</Tag>);
  }

  if (refundStatus === 3) {
    tags.push(<Tag key={refundStatus} color="orange" className="rounded-full text-xs">退款中</Tag>);
  } else if (refundStatus === 6) {
    tags.push(<Tag key={refundStatus} color="orange" className="rounded-full text-xs">已退款</Tag>);
  }

  if (goodsType === "TIMES_CARD") {
    tags.push(<Tag key={goodsType} color="cyan" className="rounded-full text-xs">次卡</Tag>);
  }else if(goodsType === "GOODS") {
    tags.push(<Tag key={goodsType} color="cyan" className="rounded-full text-xs">商品</Tag>);
  }else if (goodsType === "NORMAL") {
    tags.push(<Tag key={goodsType} color="cyan" className="rounded-full text-xs">服务</Tag>);
  }

  // 据现在多少小时
  let hours = ((Date.now() - dayjs(createTime)) / 3600 / 1000).toFixed(1);
  hours = <span className={`font-bold ${hours > 24 ? 'text-red-500' : 'text-gray-700'}`}>{hours}小时</span>;

  const onPayCancel = async (orderId, shopId) => {
    Modal.confirm({
      title: "取消订单",
      content: "确定要取消该订单吗？",
      onOk: async () => {
        let response = await fetch("/api/order/cancel", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderId,
            shopId,
          }),
        });

        let result = await response.json();

        if (result.success) {
          message.info("订单已取消！");
          onFlowChange();
        } else {
          message.error(result.data);
        }
      },
    });
  };

  const updateOrder = async (remark, orderId, shopId) => {
    const m = Modal.info({
      icon: null,
      closable: true,
      title: "确定完成收款？请备注原因",
      content: <Remarks remark={remark} orderId={orderId} shopId={shopId} prefix={"完成收款"} orderData={{ payStatus: 2 }} onChange={() => {
        m.destroy();
        onFlowChange();
      }} />,
      footer: null,
    });
  };

  const viewRemark = (remark, orderId, shopId) => {
    const m = Modal.info({
      icon: null,
      closable: true,
      title: "订单备注",
      content: <Remarks remark={remark} orderId={orderId} shopId={shopId} onChange={() => {
        m.destroy();
        onFlowChange();
      }} />,
      footer: null,
    });
  };

  const onLogisticsChange = () => {
    setLogisticsDialogOpen(false);
    onFlowChange();
  };

  return (
    <Card
      key={orderNo}
      className="w-full min-w-0 rounded-xl shadow-sm border-gray-100 h-full"
      style={{ display: 'flex', flexDirection: 'column' }}
      actions={actions}
      bodyStyle={{ padding: '12px', flex: 1, overflow: 'auto' }}
      {...others}
    >
      {/* Header */}
      <div className="flex justify-between items-center gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 truncate">
            {(appointment || goods?.appointmentInfo)?.customerName || '未知客户'}
          </h3>
        </div>
        <div className="flex flex-wrap gap-0 justify-end flex-shrink-0">
          {tags}
        </div>
      </div>

      {/* 物流对话框 */}
      { 
        goodsType === 'GOODS' 
        && orderStatus <= 8
        && <LogisticsDialog order={order} open={logisticsDialogOpen} onOk={() => onLogisticsChange()} onCancel={() => setLogisticsDialogOpen(false)} /> 
      }

      {/* Body - 紧凑布局 */}
      <div className="space-y-1.5 text-xs">
        {/* 订单号 + 手机号 */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-gray-600 truncate flex-1">单号：{orderNo}</span>
          {(appointment || goods?.appointmentInfo)?.phoneNo && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="font-mono text-gray-700">{(appointment || goods?.appointmentInfo)?.phoneNo}</span>
              <a 
                onClick={() => safeCall((appointment || goods?.appointmentInfo)?.phoneNo)} 
                className="text-blue-600 active:opacity-70 hover:text-blue-800 cursor-pointer"
              >
                <PhoneOutlined className="text-xs" />
              </a>
            </div>
          )}
        </div>

        {/* 下单时间 + 预约时间 */}
        <div className="flex items-center justify-between gap-2 text-gray-500">
          <span>下单: {dayjs(createTime).format("MM-DD HH:mm")}</span>
          {goodsType === 'NORMAL' && (appointment || goods?.appointmentInfo)?.appointmentTime && (
            <span className="text-green-600 font-medium">
              预约: {dayjs((appointment || goods?.appointmentInfo)?.appointmentTime).format("MM-DD HH:mm")}
            </span>
          )}
        </div>

        {/* 地址 */}
        {fullAddrass && (
          <div className="flex items-start gap-1.5">
            <span className="text-gray-500 flex-shrink-0">地址:</span>
            <div className="flex-1 min-w-0">
              <div className="text-gray-700 break-words line-clamp-1">{fullAddrass}</div>
            </div>
            <CopyToClipboard text={fullAddrass} onCopy={() => message.success("已复制")}>
              <span className="text-blue-600 active:opacity-70 cursor-pointer hover:text-blue-800 flex-shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </span>
            </CopyToClipboard>
          </div>
        )}

        {/* 商品信息 */}
        <div className="flex items-start gap-1.5 bg-gray-50 rounded p-1.5">
          <ShoppingOutlined className="text-green-600 text-sm mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {mutilOrder 
              ? subOrders.map((item) => { 
                  let {meta: goods, quantity, unit} = item;
                  const skuText = getSkuInfo(item);
                  return (
                    <div key={item.orderId} className="mb-0.5 last:mb-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{goods?.goodsInfo?.title}</span>
                        
                      </div>
                      {skuText && <div className="text-xs text-gray-500 mt-0.5 truncate">规格：{skuText} <span className="text-red-600">x {quantity}{unit}</span></div>}
                    </div>
                  );
                })
              : (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{goods?.goodsInfo?.title}</span>
                    
                  </div>
                  {getSkuInfo(goods) && <div className="text-xs text-gray-500 mt-0.5 truncate">规格：{getSkuInfo(goods)} <span className="text-red-600">x {quantity}{unit}</span></div>}
                </div>
              )
            }
          </div>
        </div>

        {/* 卖家备注 */}
        {/* {sellerRemark && ( */}
          <div className="bg-orange-50 rounded p-1.5 border-l-2 border-orange-400">
            <div className="flex items-start gap-1.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">商家备注 ({sellerRemark?.split("$$")?.length || 0}条)</span>
                  <span 
                    onClick={() => viewRemark(sellerRemark, orderId, shopId)}
                    className="text-blue-600 text-xs active:opacity-70 hover:text-blue-800 cursor-pointer"
                  >
                    查看/添加
                  </span>
                </div>
                <div className="text-xs text-gray-700 break-words line-clamp-1 mt-0.5">
                  {sellerRemark?.split("$$")[0]}
                </div>
              </div>
            </div>
          </div>
        {/* )} */}

        {/* 客户备注 */}
        {appointment?.remark && (
          <div className="bg-red-50 rounded p-1.5 border-l-2 border-red-400">
            <div className="flex items-start gap-1.5">
              <div className="flex-1">
                <div className="text-xs text-gray-500">客户备注</div>
                <div className="text-xs text-gray-700 break-words line-clamp-1 mt-0.5">{appointment.remark}</div>
              </div>
            </div>
          </div>
        )}

        {/* 价格信息 */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded p-2 space-y-1">
          <div className="flex justify-between items-center text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-600">应收</span>
              <span className="text-green-600">¥{(realAmount / 100).toFixed(2)}</span>
              {(orderStatus < 10 && refundStatus === 0) && (
                <span 
                  onClick={editPrice}
                  className="text-blue-600 cursor-pointer active:opacity-70 hover:text-blue-800"
                >
                  <EditOutlined className="text-xs" />改价
                </span>
              )}
            </div>
            <span className="text-gray-400 line-through">原价 ¥{(amount/100).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center pt-1 border-t border-gray-200">
            <div className="flex items-center gap-1">
              <span className="text-gray-600 font-medium">已收</span>
              <span className="text-red-600 font-bold text-base">¥{(realPayAmount / 100).toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              {(payStatus !== 2 && goodsType !== "TIMES_CARD") && (
                <span 
                  onClick={() => updateOrder(sellerRemark, orderId, shopId)}
                  className="text-blue-600 text-xs font-medium cursor-pointer active:opacity-70 hover:underline"
                >
                  确定完成收款
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 详情模式下显示的额外信息 */}
        {detail && (
          <div className="space-y-1.5 pt-2 border-t border-gray-100">
            {/* 店铺信息 */}
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-gray-50 rounded p-1.5">
                <div className="text-xs text-gray-500">服务店</div>
                <div className="text-xs text-gray-800 truncate font-medium">{fromShop?.title || '未知'}</div>
              </div>
              <div className="bg-gray-50 rounded p-1.5">
                <div className="text-xs text-gray-500">下单店</div>
                <div className="text-xs text-gray-800 truncate font-medium">{shop?.title || '未知'}</div>
              </div>
            </div>

            {/* 分销员和导购员 */}
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-gray-50 rounded p-1.5">
                <div className="text-xs text-gray-500">分销员</div>
                <div className="text-xs text-gray-700 truncate">{shareCustomer?.username || "无"}</div>
              </div>
              <div className="bg-gray-50 rounded p-1.5">
                <div className="text-xs text-gray-500">导购员</div>
                <div className="text-xs text-gray-700 truncate">{shareShop?.title || "无"}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer: 流程操作区 */}
      {(goodsType === 'NORMAL' || goodsType === 'GOODS') && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <Flex gap={1.5} vertical>
            {goodsType === 'NORMAL' && <TwoStepFlow order={order} onFlowChange={onFlowChange} times={goods?.goodsInfo?.times} />}
            {goodsType === 'GOODS' && <DeliveryFlow order={order} onFlowChange={onFlowChange} />}
          </Flex>
        </div>
      )}
    </Card>
  );
};

export default OrderCard;